import React from 'react';
import { Checkbox, ControlLink, Input, PasswordInput } from '@moda/om';
import { noop, useForm } from '@moda/portal-stanchions';
import { AuthAction } from '../../lib/authenticate';
import { useAuth } from '../../hooks/useAuth';
import { AccountLocked } from './AccountLocked';
import { SignInFrame } from './SignInFrame';
import { SignInFormValues, SignInProps } from './SignIn';

const SIGN_IN_SUBMIT = 'SIGN IN';
export const FORGOT_PASSWORD_TEXT = 'Forgot Password?';

const SIGN_IN_VALIDATIONS = {
  signInEmail: {
    email: {
      message: 'Invalid email address.',
      value: true
    },
    required: {
      message: 'Please enter an email address.',
      value: true
    }
  },
  signInPassword: {
    required: {
      message: 'Invalid password, please try again.',
      value: true
    }
  }
};

export const PasswordSignIn: React.FC<
  Required<Pick<SignInProps, 'title' | 'message'>> & SignInProps
> = ({
  onAuth,
  className,
  showSocialAuth = true,
  showCancelButton,
  defaultValues,
  showAuthModal,
  hideAuthModal,
  changePayment,
  onClickForgotPassword,
  title,
  message
}) => {
  const { error, handleAuth, loading } = useAuth(AuthAction.SignIn);

  const onSubmit = (values: SignInFormValues) => {
    handleAuth({
      email: values.signInEmail,
      password: values.signInPassword,
      remember: values.signInRemember
    })
      .then(onAuth)
      .then(changePayment)
      .catch(noop);
  };

  const { errors: fieldErrors, handleSubmit } = useForm({
    onSubmit,
    validations: SIGN_IN_VALIDATIONS
  });

  return (
    <SignInFrame
      className={className}
      error={error?.code === 'account_locked' ? <AccountLocked /> : error?.message}
      hideAuthModal={hideAuthModal}
      loading={loading}
      message={message}
      onAuth={onAuth}
      onSubmit={handleSubmit}
      showAuthModal={showAuthModal}
      showSocialAuth={showSocialAuth}
      showCancelButton={showCancelButton}
      submitLabel={SIGN_IN_SUBMIT}
      title={title}
    >
      <Input
        className="Authentication__input"
        defaultValue={defaultValues?.signInEmail}
        error={fieldErrors.signInEmail?.message || error != null}
        id="signInEmail"
        data-testid="signInEmail"
        placeholder="Email"
        type="email"
      />

      <PasswordInput
        className="Authentication__input"
        error={fieldErrors.signInPassword?.message || error != null}
        id="signInPassword"
        data-testid="signInPassword"
        placeholder="Password"
      />

      <div className="Authentication__bottomFields">
        <div className="Authentication__rememberMe">
          <Checkbox
            className="Authentication__checkbox"
            defaultChecked={defaultValues?.signInRemember ?? true}
            id="signInRemember"
            data-testid="signInRemember"
            type="checkbox"
          >
            Remember me
          </Checkbox>
        </div>

        <div className="Authentication__forgotPassword" data-testid="signInForgotPassword">
          {onClickForgotPassword ? (
            <ControlLink onClick={onClickForgotPassword} type="button">
              {FORGOT_PASSWORD_TEXT}
            </ControlLink>
          ) : (
            <ControlLink to="/forgot-password">{FORGOT_PASSWORD_TEXT}</ControlLink>
          )}
        </div>
      </div>
    </SignInFrame>
  );
};
